'use client';
import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { Loader2, Briefcase, ChevronRight, AlertCircle } from 'lucide-react';
import Link from 'next/link';

interface EngagementRow {
  id: string;
  status: string;
  engagement_type: string | null;
  created_at: string;
  expert_pseudonym: string;
  expert_badge_tier: string;
  has_qa_response: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-400/15 text-amber-400 border-amber-400/30',
  ACCEPTED: 'bg-primary/10 text-primary border-primary/30',
  IN_PROGRESS: 'bg-secondary/10 text-secondary border-secondary/30',
  COMPLETED: 'bg-green-500/10 text-green-400 border-green-500/30',
  DECLINED: 'bg-red-500/10 text-red-400 border-red-500/30',
  CANCELLED: 'bg-muted text-muted-foreground border-border',
};

export default function BuyerEngagementsListContent() {
  const supabase = createClient();
  const [engagements, setEngagements] = useState<EngagementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEngagements = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: bp } = await supabase
        .from('buyer_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!bp) { setLoading(false); return; }

      const { data: engs, error: engErr } = await supabase
        .from('engagements')
        .select(`
          id, status, engagement_type, created_at,
          expert_profiles!engagements_expert_id_fkey (pseudonym, badge_tier)
        `)
        .eq('buyer_id', bp.id)
        .order('created_at', { ascending: false });

      if (engErr) { setError(engErr.message); setLoading(false); return; }

      if (engs) {
        const enriched = await Promise.all(
          engs.map(async (eng: any) => {
            const { data: qa } = await supabase
              .from('engagement_qa_responses')
              .select('id')
              .eq('engagement_id', eng.id)
              .maybeSingle();

            return {
              id: eng.id,
              status: eng.status,
              engagement_type: eng.engagement_type,
              created_at: eng.created_at,
              expert_pseudonym: eng.expert_profiles?.pseudonym || 'Unknown Expert',
              expert_badge_tier: eng.expert_profiles?.badge_tier || 'NONE',
              has_qa_response: !!qa,
            };
          })
        );
        setEngagements(enriched);
      }
      setLoading(false);
    };
    fetchEngagements();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 size={24} className="text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="py-8 max-w-3xl mx-auto px-4">
      <div className="flex items-center gap-2.5 mb-6">
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Briefcase size={16} className="text-primary" />
        </div>
        <h1 className="font-heading text-display text-foreground">My Engagements</h1>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {engagements.length === 0 ? (
        <div className="surface-elevated p-12 text-center">
          <Briefcase size={32} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">No engagements yet</p>
          <p className="text-sm text-muted-foreground mb-4">Browse experts and send an enquiry to get started.</p>
          <Link
            href="/expert-browse-page"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all"
          >
            Browse Experts
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {engagements.map((eng) => (
            <Link
              key={eng.id}
              href={`/engagements/${eng.id}`}
              className="surface-elevated p-5 flex items-center justify-between gap-4 hover:border-primary/30 transition-colors group"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono-data text-sm font-semibold text-foreground">{eng.expert_pseudonym}</span>
                  {eng.has_qa_response && eng.status === 'ACCEPTED' && (
                    <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-400 border border-amber-400/30">
                      Response awaiting review
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {eng.engagement_type && (
                    <span className="text-xs text-muted-foreground">{eng.engagement_type}</span>
                  )}
                  <span className="text-xs text-muted-foreground/50">·</span>
                  <span className="text-xs text-muted-foreground font-mono-data">
                    {new Date(eng.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-mono-data text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[eng.status] || STATUS_COLORS.PENDING}`}>
                  {eng.status.replace('_', ' ')}
                </span>
                <ChevronRight size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
